import type { ComponentProps, SubmitEvent } from "react"
import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import BulkForm from "@/v2-backoffice-client/components/bulkForm"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import InvitationCreateTable from "@/v2-backoffice-client/invitation/create/InvitationCreateTable"
import InvitationCreateProjectFields from "@/v2-backoffice-client/invitation/create/InvitationCreateProjectFields"
import InvitationCreateFooter from "@/v2-backoffice-client/invitation/create/InvitationCreateFooter"
import {
  type InvitationProjectValues,
  useInvitationProjectValues,
} from "@/v2-backoffice-client/invitation/create/invitation.create.hooks"
import InvitationCreateTemplateField from "@/v2-backoffice-client/invitation/create/InvitationCreateTemplateField"
import InvitationCreateImport from "@/v2-backoffice-client/invitation/create/InvitationCreateImport"
import InvitationCreateTeamSlot from "@/v2-backoffice-client/invitation/create/InvitationCreateTeamSlot"
import { useInvitationTeamAutoCreate } from "@/v2-backoffice-client/invitation/create/invitation.create.autoCreate"
import CrmCreateContactCheckbox from "@/v2-backoffice-client/crm/CrmCreateContactCheckbox"
import { useContactEmailChecks } from "@/v2-backoffice-client/crm/crm.contactChecks.hooks"
import {
  useInvitationSections,
  sectionMatchStatus,
  type InvitationSection,
  SECTION_INDEX_ATTR,
} from "@/v2-backoffice-client/invitation/create/invitation.create.sections"
import {
  type InvitationRow,
  type InvitationEmailTemplateLiteral,
  DEFAULT_LANGUAGE,
  DEFAULT_EMAIL_TEMPLATE,
} from "@/v2-backoffice-client/invitation/invitation.constants"
import { describeExistingInvitees } from "@/v2-backoffice-client/invitation/invitation.utils"

export type InvitationCreateSectionData = {
  tenant: TenantFragment
  rows: InvitationRow[]
}

export type InvitationCreateFormData = InvitationProjectValues & {
  sections: InvitationCreateSectionData[]
  SendImmediately: boolean
  Language: string
  EmailTemplate: InvitationEmailTemplateLiteral
  // Create HubSpot contacts for invitees who have none (existing contacts
  // get the invitation block either way).
  CreateContact: boolean
}

export type InvitationCreateFormProps = Omit<
  ComponentProps<"form">,
  "onSubmit"
> & {
  onSubmit(
    data: InvitationCreateFormData,
    e: SubmitEvent<HTMLFormElement>,
  ): void
}

function sectionEmailRows(section: InvitationSection) {
  return section.rows.filter((row) => row.Email.trim().length > 0)
}

/**
 * The multi-team invite form: one SECTION per target team, each owning a
 * team slot and its invitee rows — "these people join team A, these join
 * team B" reads exactly like the CSM's onboarding sheet. Imported sections
 * carry a match status: auto-matched teams show it (and take an optional
 * operator "verified" stamp), several same-named teams ask for a pick, and
 * unmatched names need nothing — send creates the team, and its customer
 * when none matched, automatically. Email design, language, and send
 * timing are batch-wide.
 */
function InvitationCreateForm(props: InvitationCreateFormProps) {
  const { onSubmit, className, ...formProps } = props

  const sections = useInvitationSections()
  const project = useInvitationProjectValues()
  const [sendImmediately, setSendImmediately] = useState(true)
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE)
  const [emailTemplate, setEmailTemplate] =
    useState<InvitationEmailTemplateLiteral>(DEFAULT_EMAIL_TEMPLATE)
  const [createContact, setCreateContact] = useState(true)
  const [preparing, setPreparing] = useState(false)

  const createTeamForSection = useInvitationTeamAutoCreate()

  // One batched Backoffice + HubSpot check for every complete email across
  // all sections; each row's HubSpot column and the checkbox read from it.
  const checks = useContactEmailChecks(
    sections.sections.flatMap((section) =>
      section.rows.map((row) => row.Email),
    ),
  )

  const sendableSections = sections.sections.filter((section) => {
    if (sectionEmailRows(section).length === 0) return false
    return section.tenant !== null || sectionMatchStatus(section) === "create"
  })
  const invitationCount = sendableSections.reduce(
    (sum, s) => sum + sectionEmailRows(s).length,
    0,
  )
  const createCount = sendableSections.filter((s) => !s.tenant).length
  // Project overrides are team-scoped, so they only apply when the batch
  // targets exactly one team; multi-team batches get each team's own
  // defaults (auto-created project included).
  const singleTenant =
    sections.sections.length === 1 ? sections.sections[0].tenant : null

  async function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault()

    // Ambiguity is the one state send never resolves on its own: picking
    // among same-named teams is the operator's call.
    const ambiguous = sections.sections.filter(
      (section) =>
        sectionMatchStatus(section) === "ambiguous" &&
        sectionEmailRows(section).length > 0,
    )
    if (ambiguous.length > 0) {
      toast({
        variant: "destructive",
        title: "Pick a team first",
        description: ambiguous
          .map(
            (s) =>
              `“${s.imported?.teamName}” matches ${s.imported?.candidates.length} existing teams`,
          )
          .join(" · "),
      })
      return
    }
    if (sendableSections.length === 0) return

    setPreparing(true)
    try {
      const resolved: InvitationCreateSectionData[] = []
      for (const section of sections.sections) {
        const rows = sectionEmailRows(section)
        if (rows.length === 0) continue
        if (section.tenant) {
          resolved.push({ tenant: section.tenant, rows })
          continue
        }
        if (sectionMatchStatus(section) !== "create") continue
        const created = await createTeamForSection(section)
        // Abort on failure: the section keeps its state, the error toast
        // says why, and already-created teams stay slotted in for a retry.
        if (!created) return
        sections.setSectionTenant(sections.sections.indexOf(section), created)
        resolved.push({ tenant: created, rows })
      }
      if (resolved.length === 0) return

      onSubmit(
        {
          sections: resolved,
          ...project.values,
          SendImmediately: sendImmediately,
          Language: language,
          EmailTemplate: emailTemplate,
          CreateContact: createContact,
        },
        e,
      )
    } finally {
      setPreparing(false)
    }
  }

  return (
    <BulkForm
      className={cn("invitation-create-form", className)}
      onPaste={sections.handlePaste}
      onSubmit={(e) => void handleSubmit(e)}
      {...formProps}
    >
      <InvitationCreateImport onImported={sections.loadSections} />

      <div className="mt-4 flex flex-col gap-4">
        {sections.sections.map((section, index) => (
          <section
            key={section.key}
            {...{ [SECTION_INDEX_ATTR]: index }}
            className="flex flex-col gap-2 rounded-lg border border-border p-3"
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <span className="invitation-create-form-label whitespace-nowrap">
                Team *
              </span>
              <InvitationCreateTeamSlot
                section={section}
                onSelectTenant={(tenant) => {
                  sections.setSectionTenant(index, tenant)
                  if (!tenant && sections.sections.length === 1) {
                    project.reset()
                  }
                }}
                onVerify={() => {
                  sections.setSectionVerified(index, true)
                }}
              />
              <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
                {section.rows.filter((r) => r.Email.trim()).length}{" "}
                invitation(s)
              </span>
              {sections.sections.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="tiny"
                  iconSrc="trash"
                  aria-label="Remove this team from the batch"
                  onClick={() => {
                    sections.removeSection(index)
                  }}
                />
              )}
            </div>

            {singleTenant && index === 0 && (
              <InvitationCreateProjectFields
                className="invitation-create-form-project-nested"
                tenantId={singleTenant.Id}
                projectId={project.projectId}
                onProjectIdChange={project.setProjectId}
                autoCreateProject={project.autoCreateProject}
                onAutoCreateProjectChange={project.setAutoCreateProject}
                countryCode={project.countryCode}
                onCountryCodeChange={project.setCountryCode}
              />
            )}

            <InvitationCreateTable
              rows={section.rows}
              onUpdateRow={(rowIndex, field, value) => {
                sections.updateRow(index, rowIndex, field, value)
              }}
              onRemoveRow={(rowIndex) => {
                sections.removeRow(index, rowIndex)
              }}
              checks={checks}
              createContact={createContact}
            />

            <div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconSrc="plus"
                onClick={() => {
                  sections.addRow(index)
                }}
              >
                Add row
              </Button>
            </div>
          </section>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconSrc="plus"
          onClick={sections.addSection}
        >
          Add Another Team
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconSrc="refresh"
          onClick={() => {
            sections.reset()
            project.reset()
          }}
        >
          Reset
        </Button>
        <span className="text-xs text-muted-foreground">
          {invitationCount} invitation(s)
          {createCount > 0 && ` · ${createCount} new team(s) on send`}
          {preparing && " · creating teams..."} · Tip: you can paste a table to
          populate multiple rows at once
        </span>
      </div>

      <InvitationCreateTemplateField
        value={emailTemplate}
        onChange={setEmailTemplate}
      />

      {/* A fixed slot, so the warning appearing never moves the form. */}
      <span className="bulk-form-warning mt-3 block h-5 truncate">
        {describeExistingInvitees(checks.existingUserCount)}
      </span>

      <CrmCreateContactCheckbox
        className="mt-3 rounded-md border border-border px-3"
        checked={createContact}
        onCheckedChange={setCreateContact}
        counts={
          checks.answered
            ? {
                new: checks.newCount,
                contacts: checks.existingContactCount,
                users: checks.existingUserCount,
              }
            : undefined
        }
      />

      <InvitationCreateFooter
        sendImmediately={sendImmediately}
        onSendImmediatelyChange={setSendImmediately}
        language={language}
        onLanguageChange={setLanguage}
        hasValidRows={invitationCount > 0}
      />
    </BulkForm>
  )
}

export default InvitationCreateForm
